/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import CardEducation from '@bvt-shared/component/CardEducation';
import CardProfession from '@bvt-shared/component/CardProfession';
import CardLandPrice from '@bvt-shared/component/CardLandPrice';
import CardReligion from '@bvt-shared/component/CardReligion';
import CardZoning from '@bvt-shared/component/CardZoning';
import { useProject } from '@bvt-features/mapp/project/hooks/useProject';
import {
  CardSesDomination,
  CardSesResult,
} from '@bvt-shared/component/CardSesDomination';
import { CardPoiGet } from '@bvt-shared/component/CardPoiGet';
import { CardGenderByAge } from '@bvt-shared/component/CardGenderByAge';
import { useLegend } from '@bvt-features/mainmap/legend/hooks/useLegend';
import { getIcon } from '@bvt-shared/utils/iconUtils';
import { CardMobileData } from '@bvt-shared/component/CardMobileData';
import { CardPeopleDensity } from '@bvt-shared/component/CardPeopleDensity';
import { professionMappers } from '@bvt-features/mapana/helper/professionMappers';
import _ from 'lodash';
import { useGRIANA } from '@bvt-features/mapana/griana/hook';
import { GRIDPRO_STYLE } from '@bvt-features/mapana/griana/constant/GRIDPRO_STYLE';
import {
  AreaInfo,
  DemoCath,
  GridInfo,
} from '@bvt-features/mapana/griana/component/GrianaStepSix';
import { ResultGriana } from '@bvt-features/pertamina/components/ResultGriana';
import { usePlace } from '@bvt-features/mainmap/place/hooks/usePlace';
import { ID_SOURCE_CONSTANT } from '@bvt-shared/constant/ID_SOURCE_CONSTANT';
import { useNavigate } from 'react-router';

/**
 *
 * @param {object} props
 * @param {string} props.projectID
 * @param {string} props.processID
 * @param {() => void} props.onBack
 */

export const PertaminaResultGrianaContainer = (props) => {
  const { processID, projectID, onBack } = props;

  const GRIANA = useGRIANA();
  const LEGEND = useLegend();
  const PROJECT = useProject();
  const PLACE = usePlace();
  const navigate = useNavigate();

  const [listGid, setListGid] = useState([]);

  const PROPERTIES = GRIANA.state.gridDetail;
  const SES_NEW = PROPERTIES?.ses_new ?? [];

  const showZeroSubtotal = (data, tpz) => {
    if (tpz === false) {
      return data
        .map((industry) => {
          const filteredGroups = industry.groups.filter(
            (group) => group.subTotal !== 0
          );
          return {
            ...industry,
            groups: filteredGroups,
          };
        })
        .filter((industry) => industry.groups.length > 0);
    } else {
      return data;
    }
  };

  useEffect(() => {
    if (projectID && processID) {
      const param = {
        page: 1,
        pageSize: 500,
        process_id: processID,
        project_id: projectID,
      };
      GRIANA.setIsShownOnMap(true);
      GRIANA.getGridCollections(param);
      PROJECT.getDetail(projectID);
    } else {
      GRIANA.prevStep();
    }

    return () => {
      PROJECT.resetState();
      PLACE.resetAll();
      GRIANA.resetAll();
      GRIANA.setIsShownOnMap(false);
    };
  }, [projectID]);

  useEffect(() => {
    if (PROJECT.state.status_DETAIL === 'SUCCESS' && projectID && processID) {
      const placeParams = {
        meta: {
          filter: {
            id_province: PROJECT.state?.detail?.form_set?.province?.id,
            id_city: PROJECT.state?.detail?.form_set?.regency?.id,
            id_source: ID_SOURCE_CONSTANT,
          },
        },
      };
      PLACE.get_advance({ params: placeParams });
    }
  }, [PROJECT.state.status_DETAIL]);

  useEffect(() => {
    const { polygon_provinsi, polygon_kota } = PLACE.state.advance?.list || {};

    if (polygon_provinsi?.properties) {
      const { id, kode, displayName } = polygon_provinsi.properties;
      PLACE.setParams({
        type: 'provinsi',
        page: 1,
        id_provinsi: id,
        kode_provinsi: kode,
        nama_provinsi: displayName,
      });
    }

    if (polygon_kota?.properties) {
      const { id, kode, displayName } = polygon_kota.properties;
      const provinsiId = polygon_provinsi?.properties?.id;
      PLACE.setParams({
        type: 'kota',
        page: 1,
        id_kota: id,
        id_provinsi: provinsiId,
        kode_kota: kode,
        nama_kota: displayName,
      });
    }
  }, [PLACE.state.advance?.list]);

  useEffect(() => {
    const properties = GRIANA.state.geojson?.features?.map(
      (feature) => feature.properties
    );
    if (properties) {
      const list = properties.map((prop) => ({
        label: `GRID - ${prop.gid}`,
        value: `${prop.gid}`,
        acceptable: prop.skala_result,
      }));
      const sortedList =
        _.sortBy(list, (vs) => {
          return vs?.acceptable;
        }) || [];
      GRIANA.setGid(+sortedList[0]?.value);
      setListGid(sortedList);
    }
  }, [GRIANA.state.status_GET_AREA]);

  useEffect(() => {
    if (GRIANA.state.gid && processID && projectID) {
      GRIANA.getGridDetail({
        gid: GRIANA.state.gid,
        uuid: processID,
      });
      if (!GRIANA.state?.selected_gid.includes(GRIANA.state.gid)) {
        GRIANA.selectGID(GRIANA.state.gid);
        GRIANA.getPoiCollections({
          gid: GRIANA.state.gid,
          limit: 1000,
          page: 1,
          project_id: projectID,
          process_id: processID,
        });
      }
    }
  }, [GRIANA.state.gid]);

  const nilaiTanah = `${PROPERTIES?.nilai_tanah}`
    .replace('Rp.', '')
    .split(',')
    .join('.');

  const getPercent = (value, total) => {
    if (!value) {
      return '0';
    }
    return ((value / total) * 100).toFixed(2);
  };

  const educationList = () => {
    if (PROPERTIES?.pendidikan) {
      const education = PROPERTIES?.pendidikan;
      const total = Object.values(education).reduce((a, b) => a + b, 0);
      return [
        {
          color: '#12B2A7',
          type: 'NON SCHOOL',
          percent: getPercent(education.BELUM_TAMA, total),
        },
        {
          color: '#49B6C5',
          type: 'ELEMENTARY SCHOOL',
          percent: getPercent(education.TAMAT_SD, total),
        },
        {
          color: '#5CA3E5',
          type: 'JUNIOR HIGHSCHOOL',
          percent: getPercent(education.SLTP, total),
        },
        {
          color: '#00A8FF',
          type: 'SENIOR HIGHSCHOOL',
          percent: getPercent(education.SLTA, total),
        },
        {
          color: '#8000FF',
          type: 'DIPLOMA 1,2 & 3',
          percent: getPercent(
            education.DIPLOMA_I +
              education.DIPLOMA_II +
              (education.DIPLOMA_III || 0),
            total
          ),
        },
        {
          color: '#9F5EE0',
          type: 'UNIVERSITY',
          percent: getPercent(
            education.STRATA_I + education.STRATA_II + education.STRATA_III,
            total
          ),
        },
      ];
    }
    return undefined;
  };

  const prof = () => professionMappers(PROPERTIES?.pekerjaan);

  const religion = () => {
    const agama = PROPERTIES?.agama;
    return [
      { type: 'Islam', value: agama?.ISLAM, color: '#006C64' },
      { type: 'Budha', value: agama?.BUDHA, color: '#33CC99' },
      { type: 'Catholic', value: agama?.KATHOLIK, color: '#FF9966' },
      { type: 'Confucianism', value: agama?.KONGHUCU, color: '#FF0066' },
      { type: 'Hindu', value: agama?.HINDU, color: '#FFCC00' },
      { type: 'Protestant', value: agama?.KRISTEN, color: '#00A8FF' },
      { type: 'Others', value: agama?.KEPERCAYAAN, color: '#A1ADBB' },
    ];
  };

  const ageRange = () => {
    const ageRangeCategory = PROPERTIES?.age_range_category;
    let sumTotal = 0;
    if (ageRangeCategory) {
      Object.values(ageRangeCategory['total']).map((item) => {
        sumTotal += item?.value;
      });
      const arrOfKeys = Object.keys(ageRangeCategory?.['total']);
      return _.map(arrOfKeys, (v) => {
        return {
          key: v,
          male: {
            value: ageRangeCategory?.['male']?.[v]?.value,
            percentage:
              (ageRangeCategory?.['male']?.[v]?.value / sumTotal) * 100,
          },
          female: {
            value: ageRangeCategory?.['female']?.[v]?.value,
            percentage:
              (ageRangeCategory?.['female']?.[v]?.value / sumTotal) * 100,
          },
          total: {
            value: ageRangeCategory?.['total']?.[v]?.value,
            percentage:
              (ageRangeCategory?.['total']?.[v]?.value / sumTotal) * 100,
          },
        };
      });
    }
    return undefined;
  };

  const sesDomination = () => {
    const colors = {
      low: '#FF3939',
      medium_low: '#FF9317',
      medium_high: '#07827C',
      high: '#00A8FF',
    };
    if (PROPERTIES?.social_economi_status) {
      const sesObj = PROPERTIES?.social_economi_status;
      const mappedSesObj = [
        {
          label: 'Low',
          value: sesObj?.['Low'],
          color: colors['low'],
        },
        {
          label: 'Medium Low',
          value: sesObj?.['Medium-Low'],
          color: colors['medium_low'],
        },
        {
          label: 'Medium High',
          value: sesObj?.['Medium-High'],
          color: colors['medium_high'],
        },
        {
          label: 'High',
          value: sesObj?.['High'],
          color: colors['high'],
        },
      ];
      return mappedSesObj;
    }
    return undefined;
  };

  const poiList = () => {
    const groupByIndustry = _.groupBy(
      _.filter(
        GRIANA.state?.poiCollections,
        (v) => v?.properties?.gid === GRIANA?.state?.gid
      ),
      (v) => v?.properties?.industry_code
    );

    return _.map(groupByIndustry, (v) => {
      return {
        industryCode: v?.[0]?.properties?.industry_code,
        name: v?.[0]?.properties?.industry_name,
        color: v?.[0]?.properties?.category_icon_color,
        groups: _.flatMap(
          _.groupBy(v, (w) => w?.properties?.group_code),
          (group) => {
            return _.map(
              _.groupBy(group, (w) => w?.properties?.category_code),
              (category) => {
                return {
                  groupName: group?.[0]?.properties?.group_name,
                  categoryName: category?.[0]?.properties?.category_name,
                  icon: category?.[0]?.properties?.category_icon,
                  brands: _.map(
                    _.groupBy(category, (w) => w?.properties.brand_code),
                    (brand) => {
                      return {
                        brandName: brand?.[0]?.properties.brand_name,
                        subTotal: brand.length,
                      };
                    }
                  ),
                };
              }
            );
          }
        )
          .map((group) => {
            return group.brands.map((brand) => {
              return {
                ...brand,
                ...group,
              };
            });
          })
          .flat(),
      };
    });
  };

  useEffect(() => {
    LEGEND.isLoadingData(true);
    LEGEND.removeDataByPrefix('GRIANA');
    if (GRIANA.state.status_DETAIL === 'SUCCESS') {
      let counterIndex = 0;
      const tempGrid = GRIDPRO_STYLE.map((item) => {
        return {
          key: `GRIANA_` + 'SQUARE_' + String(counterIndex++),
          type: 'SQUARE',
          title: item.gridStatus,
          color: item.style.selected.fillColor,
          group: 'Score',
        };
      });
      const mappedPOI = _.uniqBy(
        _.map(
          _.sortBy(
            GRIANA.state?.poiCollections,
            (v) => v.properties.category_code
          ),
          (v) => {
            return {
              key: `GRIANA_POI_${v?.properties?.category_name}`,
              title: v?.properties?.category_name,
              type: 'ICON',
              icon: (
                <img
                  alt='icon-griana'
                  src={getIcon('poi', v?.properties?.category_icon)}
                  style={{ width: '1rem' }}
                />
              ),
              group: 'Point of Interest',
            };
          }
        ),
        (v) => v.key
      );
      LEGEND.registerData({
        data: {
          key: 'GRIANA',
          title: 'Grid Analysis',
          items: [...mappedPOI, ...tempGrid],
        },
      });
      LEGEND.isLoadingData(false);
      LEGEND.toggleOpenLegend(true);
    }
  }, [GRIANA.state.status_DETAIL, GRIANA.state?.poiCollections]);

  return (
    <ResultGriana
      dataPreview={{
        selectOption: {
          data: listGid,
          value: GRIANA.state.gid.toString(),
          onChange: (e) => {
            GRIANA.setGid(+e);
          },
        },
      }}
      isAreaLoading={GRIANA.state.status_GET === 'LOADING'}
      isDetailLoading={
        GRIANA.state.status_DETAIL !== 'SUCCESS' ||
        GRIANA.state?.status_GET_POI !== 'SUCCESS'
      }
      onBack={onBack}
      showDashboard={() => {
        navigate(`/app/dashboard?step=6&project_id=${projectID}&process=${processID}&type=nfr`);
      }}
    >
      <AreaInfo
        data={{
          areaName: GRIANA?.state?.gridDetail?.desa,
          province: GRIANA?.state?.gridDetail?.provinsi,
          regency: GRIANA?.state?.gridDetail?.kabkot,
          district: GRIANA?.state?.gridDetail?.kecamatan,
          village: GRIANA?.state?.gridDetail?.desa,
          areaTag: [
            GRIANA?.state?.gridDetail?.kecamatan,
            GRIANA?.state?.gridDetail?.kabkot,
            GRIANA?.state?.gridDetail?.provinsi,
          ],
        }}
        isLoading={[
          GRIANA.state.status_DETAIL,
          GRIANA.state.status_GET_AREA,
        ].includes('LOADING')}
      />
      <GridInfo
        acceptable={PROPERTIES?.skala_result}
        center={`${PROPERTIES?.grid_x || 0}, ${PROPERTIES?.grid_y || 0}`}
        grid={`${PROPERTIES?.gid}`}
        max={PROPERTIES?.gridStatus?.max?.toFixed(2)}
        min={PROPERTIES?.gridStatus?.min?.toFixed(2)}
        size={`${PROPERTIES?.grid_size}`}
        totalScore={Math.round(PROPERTIES?.total_param)?.toFixed(2)}
      />
      <CardPoiGet
        data={showZeroSubtotal(
          GRIANA?.state?.gridDetail?.poiResults ?? poiList(),
          false
        )}
        extraLabel={
          <span>
            Grid <b className='bold-green'>{GRIANA.state?.gid}</b>
          </span>
        }
        isLoading={
          GRIANA.state?.status_GET_POI === 'LOADING' ||
          GRIANA.state?.status_DETAIL === 'LOADING'
        }
      />
      {PROPERTIES?.people_density &&
        !GRIANA?.state?.gridDetail?.peopleDensity_time && (
        <CardPeopleDensity
          detailInfo={{
            catchmentBy: PROJECT.state.detail?.form_set?.density?.resultType,
          }}
          isLoading={
            PROPERTIES?.people_density &&
              PROJECT.state?.status_GET === 'LOADING'
          }
          value={PROPERTIES?.people_density}
        />
      )}
      {!!GRIANA?.state?.gridDetail?.peopleDensity_time && (
        <CardMobileData
          colorEvent={{
            main: '#33CC33',
            secondary: '#ADEBAD',
            hover: '#1D941D',
          }}
          colorUnique={{
            main: '#2CAFFE',
            secondary: '#2CAFFE66',
            hover: '#149AEB',
          }}
          data={GRIANA?.state?.gridDetail?.peopleDensity_time}
          isLoading={[
            GRIANA.state.status_DETAIL,
            GRIANA.state.status_GET_AREA,
          ].includes('LOADING')}
          periodic={GRIANA?.state?.gridDetail?.peopleDensity_time?.type}
          title='Mobile Data'
        />
      )}
      {PROPERTIES?.demography_persentase && (
        <DemoCath
          femaleCount={PROPERTIES?.demography_persentase?.wanita}
          maleCount={PROPERTIES?.demography_persentase?.pria}
          totalCount={PROPERTIES?.demography_persentase?.total}
        />
      )}

      {'ses_new' in PROPERTIES ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          {SES_NEW?.length > 0 && (
            // eslint-disable-next-line max-lines
            <>
              {SES_NEW?.map((v) => (
                <CardSesResult
                  data={v.data}
                  isLoading={
                    PROJECT?.state?.status_GET === 'LOADING' &&
                    !PROPERTIES?.ses_new
                  }
                  key={v.title}
                  title={v.title}
                />
              ))}
            </>
          )}
        </div>
      ) : (
        <>
          {'social_economi_status' in PROPERTIES && (
            <>
              {Object.keys(PROPERTIES?.social_economi_status)?.length > 0 && (
                <CardSesDomination
                  data={sesDomination()}
                  isLoading={
                    !PROPERTIES?.social_economi_status &&
                    PROJECT?.state?.status_GET === 'LOADING'
                  }
                  isShowPie={false}
                />
              )}
            </>
          )}
        </>
      )}

      {PROPERTIES?.age_range_category && (
        <CardGenderByAge
          data={ageRange()}
          isLoading={
            (!PROPERTIES?.age_range_category &&
              GRIANA.state?.status_GET_POI === 'LOADING') ||
            GRIANA.state?.status_DETAIL === 'LOADING'
          }
        />
      )}
      <CardLandPrice id='GrianaSidebarLandPrice' value={nilaiTanah} />
      <CardEducation data={educationList()} id='GrianaSidebarEducation' />
      <CardReligion data={religion()} id='GrianaSidebarReligion' />
      <CardProfession
        id='GrianaSidebarProfession'
        other={prof().other}
        topSix={prof()?.topSix}
      />
      <CardZoning data={PROPERTIES?.RDTR} id='GrianaSidebarZoning' />
    </ResultGriana>
  );
};
